import { type BooleanElementGroupType } from '../plugins/boolean';
import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore, type ElementMap } from '../stores/useEditorStore';
import { uid } from '../utils/uid';

import { renderStaticScene } from './scene';
import { type GenericShape } from './shapes';
import { type Zoom } from './zoom';

export interface GenericElement {
  angle: number;
  height: number;
  id: string;
  name: string;
  isDeletedFlag: boolean;
  isVisible: boolean;
  isLocked: boolean;
  originX: number;
  originY: number;
  scale?: [x: number, y: number];
  shape: GenericShape;
  type: string;
  layer: number;
  opacity: number;
  renderedById?: string;
  booleanMode?: BooleanElementGroupType;
  /// There are multiple caches throughout the codebase. Update the hash of the type to invalidate the cache.
  version: {
    /// When boundaries changed. E.g. when the element is resized, points are changed, etc.
    bounds: string;
    /// When the element is changed. E.g. when the element is moved, rotated, scaled
    canvas: string;
    /// When underlying path is changed. E.g. when points get resized
    path: string;
  };
  width: number;
  x: number;
  y: number;
}

export type ElementCanvas = {
  canvas: HTMLCanvasElement;
  canvasOffsetX: number;
  canvasOffsetY: number;
  padding: number;
  scale: number;
  zoom: Zoom;
};

export type ElementVersion = keyof GenericElement['version'];

let layer = 1;
let retouchLayer = -1;

export const sortElementsByLayer = (
  a: GenericElement,
  b: GenericElement,
): number => {
  // Layer 0 (image) always renders first
  if (a.layer === 0) return -1;
  if (b.layer === 0) return 1;

  // Reverse sort for retouch layers (more negative = newer = on top)
  if (a.layer < 0 && b.layer < 0) {
    return b.layer - a.layer;
  }

  // Mixed: retouch layers always below regular layers after the image
  if (a.layer < 0 && b.layer > 0) return -1; // a (retouch) before b (regular)
  if (a.layer > 0 && b.layer < 0) return 1; // b (retouch) before a (regular)

  // Normal sort for regular layers
  return a.layer - b.layer;
};

export const isLayerBelow = (
  elementLayer: number,
  targetLayer: number,
): boolean => {
  // Layer 0 (image) always below everything else
  if (targetLayer === 0) return false;
  if (elementLayer === 0) return true;

  // Reverse logic for retouch layers (more negative = newer = on top)
  if (elementLayer < 0 && targetLayer < 0) {
    return elementLayer > targetLayer;
  }

  // Mixed: retouch layers always below regular layers
  if (elementLayer > 0 && targetLayer < 0) return false; // retouch below regular
  if (elementLayer < 0 && targetLayer > 0) return true; // regular above retouch

  // Normal logic for regular layers
  return elementLayer < targetLayer;
};

export const resetLayerCounter = () => {
  const { elements } = useEditorStore.getState();

  let highestPositiveLayer = Math.max(
    1,
    ...Array.from(elements.values())
      .filter((el) => el.layer > 0)
      .map((el) => el.layer),
  );

  for (const element of elements.values()) {
    if (element.type !== 'image' && element.layer === 0) {
      element.layer = ++highestPositiveLayer;
    }
  }

  const maxLayer = Math.max(
    0,
    ...Array.from(elements.values())
      .filter((el) => el.layer > 0)
      .map((el) => el.layer),
  );
  layer = maxLayer + 1;

  const minRetouchLayer = Math.min(
    0,
    ...Array.from(elements.values())
      .filter((el) => el.layer < 0)
      .map((el) => el.layer),
  );
  retouchLayer = minRetouchLayer - 1;
};

export const getNextRetouchLayer = () => retouchLayer--;

export const defaultGenericElement = (): GenericElement => {
  const id = uid(5);

  return {
    angle: 0,
    height: 0,
    name: id,
    id,
    layer: layer++,
    isDeletedFlag: false,
    opacity: 1,
    isLocked: false,
    isVisible: true,
    originX: 0,
    originY: 0,
    type: '',
    booleanMode: 'intersect',
    shape: {
      data: '',
      type: 'area',
    },
    version: {
      bounds: `bounds-${id}-${uid(3)}`,
      canvas: `canvas-${id}-${uid(3)}`,
      path: `path-${id}-${uid(3)}`,
    },
    width: 0,
    x: 0,
    y: 0,
  };
};

export const hasAssignedName = (element: GenericElement): boolean =>
  element.name !== element.id;

export const newElementVersion = (
  element: GenericElement,
  type: ElementVersion,
) => {
  return `${type}-${element.id}-${uid(3)}`;
};

export const reversionElement = (
  element: GenericElement,
  types: ElementVersion[],
) => {
  return {
    version: types.reduce(
      (acc, type) => ({ ...acc, [type]: newElementVersion(element, type) }),
      element.version,
    ),
  };
};

let updateCounter = 0;

export const notifyElementsChanged = () => {
  useEditorStore.setState(() => ({
    lastUpdateHash: `${++updateCounter}`,
  }));
};

export const updateElement = <TElement extends GenericElement = GenericElement>(
  elements: ElementMap,
  original: TElement,
  update: Partial<TElement>,
  forceRerender?: boolean,
) => {
  const element = elements.get(original.id);
  if (!element) return;
  const newElement = { ...element, ...update };
  elements.set(original.id, newElement);

  useEditorRuntimeStore.getState().onElementEvent?.('updated', newElement);
  notifyElementsChanged();

  if (forceRerender) {
    renderStaticScene(true);
  }
};

export const updateMultipleElements = (
  elements: ElementMap,
  originals: GenericElement[],
  updates: Partial<GenericElement>[],
) => {
  const updatedElements: GenericElement[] = [];
  originals.forEach((original, index) => {
    const newElement = { ...original, ...updates[index] };
    elements.set(original.id, newElement);
    updatedElements.push(newElement);
  });
  // Fire events after all elements are updated so that reevaluateDimensions
  // sees the final positions on the first call and short-circuits the rest
  const onElementEvent = useEditorRuntimeStore.getState().onElementEvent;
  if (onElementEvent) {
    for (const newElement of updatedElements) {
      onElementEvent('updated', newElement);
    }
  }
};

export const addElement = (elements: ElementMap, element: GenericElement) => {
  elements.set(element.id, element);
  useEditorRuntimeStore.getState().onElementEvent?.('created', element);
  notifyElementsChanged();
};

export const deleteMultipleElements = (
  elements: ElementMap,
  idsToDelete: string[],
) => {
  idsToDelete.forEach((id) => {
    const deletedElement = { ...elements.get(id)! };

    if (elements.delete(id)) {
      useEditorRuntimeStore
        .getState()
        .onElementEvent?.('deleted', deletedElement);
    }
  });
};
